import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PlainDatePipe } from '@common/pipes/plain-date.pipe';
import { ReservationDTO } from '@gen/reservation/reservation.dto';
import { ReservationService } from '@gen/reservation/reservation.service';
import { Beds24ReservationStatus, EntityType } from '@gen-common/enums';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { AtomicsModule } from 'src/app/atomics/atomics.module';

import { PermissionService } from '../setting/permission.service';

import { getReservationNights } from '@/shared/entity-utils';
import { DateRange, Filter } from '@/shared/filter';
import { PermissionKey } from '@/shared/models/permission.structure';
import { PlainDateUtil } from '@/shared/plain-date-util';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TranslocoModule,
    PlainDatePipe,
    AtomicsModule,
    SkeletonModule
  ],
  providers: [ReservationService],
  standalone: true
})
export class HomeComponent implements OnInit {
  translocoService = inject(TranslocoService);
  router = inject(Router);

  entityService = inject(ReservationService);
  permissionService = inject(PermissionService);

  entityName = 'reservation';
  entityType = EntityType.RESERVATION;
  checkIns: ReservationDTO[] = [];
  checkOuts: ReservationDTO[] = [];
  checkInsLoading = false;
  checkOutsLoading = false;
  skeletonRows = Array.from({ length: 20 });
  checkInSkeletonColumns = Array.from({ length: 7 });
  checkOutSkeletonColumns = Array.from({ length: 5 });

  async ngOnInit(): Promise<void> {
    await Promise.all([this.loadCheckIns(), this.loadCheckOuts()]);
  }

  private async loadCheckIns(): Promise<void> {
    this.checkInsLoading = true;
    const checkInFilter = new Filter<ReservationDTO>();
    checkInFilter.sort.orderByDesc = false;
    checkInFilter.params['beds24_status'] = [Beds24ReservationStatus.CONFIRMED, Beds24ReservationStatus.UNKNOWN] as any;
    // Check-ins
    checkInFilter.sort.field = 'arrival_date';
    const arrivalDateRange: DateRange = {
      from: PlainDateUtil.today(),
      to: PlainDateUtil.addDays(PlainDateUtil.today(), 10)
    };
    checkInFilter.dateRanges = {
      arrival_date: arrivalDateRange
    };
    checkInFilter.params['arrival_date'] = 'active' as any;
    try {
      this.checkIns = await this.entityService.getFilteredAsArray(checkInFilter);
    } finally {
      this.checkInsLoading = false;
    }
  }

  private async loadCheckOuts(): Promise<void> {
    this.checkOutsLoading = true;
    const checkOutFilter = new Filter<ReservationDTO>();
    checkOutFilter.sort.field = 'departure_date';
    checkOutFilter.params['beds24_status'] = [Beds24ReservationStatus.CONFIRMED, Beds24ReservationStatus.UNKNOWN] as any;
    const departureDateRange: DateRange = {
      from: PlainDateUtil.today(),
      to: PlainDateUtil.addDays(PlainDateUtil.today(), 10)
    };
    checkOutFilter.dateRanges = {
      departure_date: departureDateRange
    };
    checkOutFilter.params['departure_date'] = 'active' as any;
    try {
      this.checkOuts = await this.entityService.getFilteredAsArray(checkOutFilter);
    } finally {
      this.checkOutsLoading = false;
    }
  }

  hasPermission(): boolean {
    return this.permissionService.hasPermission(EntityType.RESERVATION, [PermissionKey.VIEW], true);
  }

  viewEntity(event: TableRowSelectEvent<ReservationDTO>) {
    const entity = event.data as ReservationDTO;
    if (entity?.id) {
      void this.router.navigate([`/${this.entityName}`, entity.id]);
    }
  }

  nightsNumber(reservation: ReservationDTO): number {
    return getReservationNights(reservation);
  }
}
